import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Snackbar, styled, Typography } from '@mui/material'
import { InfiniteData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'

import {
  CustomerService,
  ITransactionsResponse,
  nextTransactionsCursor,
  TRANSACTIONS_PAGE_SIZE,
  TransactionsCursor,
} from '@/api/services/CustomerService'
import { BankService } from '@/api/services/BankService'
import { connectionQueryKeys } from '@/api/services/connectionQueryKeys'
import { ErrorPage } from '@/pages/Error'
import { ITransaction } from '@/types'
import { useAppStore } from '@/store'
import { PlatformPermission } from '@/domain/permissions/rbac'
import { usePermissions } from '@/domain/permissions/usePermissions'
import SearchLogo from '@/assets/search.svg'
import { DataTable, Order } from '../UI/DataTable'
import { CursorPaginator } from '../UI/Paginator/CursorPaginator'
import { Paginator } from '../UI/Paginator'
import { Search } from '../UI/Search'
import { transactionColumns } from './Columns'
import { BulkAliasBar } from './BulkAliasBar'
import { ReviewAliasDialog } from './ReviewAliasDialog'
import { aliasLabel, DescriptionAlias, transactionKey, transactionLabel } from './aliasOptions'
import { errorMessage } from './bulkAlias'
import { useBulkAliasUpdate } from './useBulkAliasUpdate'
import { TransactionFilters } from './Filters'
import {
  applyFilters,
  EMPTY_FILTERS,
  filterOptions,
  hasAmountFilter,
  narrowsRows,
  ITransactionFilters,
} from './filterModel'
import {
  DEFAULT_SORT_BY,
  DEFAULT_SORT_ORDER,
  isDefaultSort,
  sortTransactions,
  TransactionSortKey,
} from './sortModel'

// A search, filter or sort reads every page before answering, since one read covers a single account
// and a match can sit in any of them. The cap is a backstop against a token that never empties
// rather than a limit meant to be reached; when it does bite, the scope note says so instead of
// passing off a partial read as the whole one. Counted in backend calls rather than reads, because
// one read chains several calls to fill a batch; the read that crosses it may overrun it by at
// most the calls one read can chain.
export const MAX_NARROWED_REQUESTS = 100

type TransactionPages = InfiniteData<ITransactionsResponse, TransactionsCursor>

// Keeps only the first read of an accumulated list. React Query refetches every cached page of an
// infinite query when it goes stale, so a list a search walked end to end would otherwise be walked
// again in full the next time the tab is opened.
const toFirstRead = (cached: TransactionPages | undefined): TransactionPages | undefined =>
  cached && cached.pages.length > 1
    ? { pages: cached.pages.slice(0, 1), pageParams: cached.pageParams.slice(0, 1) }
    : cached

export const Transactions: React.FC = () => {
  // The reads below resolve the customer from IndexedDB rather than taking an argument, so the id
  // has to reach the query keys some other way or one customer's rows are served for another.
  const { user } = useAppStore()
  const userId = user?.id
  const queryClient = useQueryClient()

  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchPage, setSearchPage] = useState(1)
  const [sortBy, setSortBy] = useState<TransactionSortKey>(DEFAULT_SORT_BY)
  const [sortOrder, setSortOrder] = useState<Order>(DEFAULT_SORT_ORDER)
  const [filters, setFilters] = useState<ITransactionFilters>(EMPTY_FILTERS)
  // Held by transaction key, so rows stay picked across pages and sorting.
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  // The rows under review are fixed when the review opens, so what is sent is what was shown.
  const [review, setReview] = useState<{ alias: DescriptionAlias; rows: ITransaction[] } | null>(
    null
  )
  const [reviewFailure, setReviewFailure] = useState<{ message: string; updatedCount: number }>()
  const [notice, setNotice] = useState('')
  const { apply, reconcile, isApplying, progress } = useBulkAliasUpdate(userId)
  // Changing an alias is a Support action; everyone else sees the table read-only.
  const { user: operator } = useAuth0()
  const { hasPermission } = usePermissions(operator?.['https://carringtonlabs.io/appRole'])
  const canUpdateAliases = hasPermission(PlatformPermission.Support)
  // Read after an update finishes, to tell whether the customer it was for is still the one shown.
  const shownUserId = useRef(userId)
  shownUserId.current = userId

  // Reads accumulate into one list rather than being shown a batch at a time. How much one read
  // returns is a matter of how many round trips it takes to fill the list, and never shows up in
  // where the pages fall -- a page is short only when the customer has no more transactions.
  const { data, isLoading, isFetchingNextPage, isError, hasNextPage, fetchNextPage } =
    useInfiniteQuery<
      ITransactionsResponse,
      Error,
      { pages: ITransactionsResponse[] },
      readonly unknown[],
      TransactionsCursor
    >({
      // userId is part of the key so a different customer never reads these accumulated pages.
      queryKey: ['transactions', userId],
      queryFn: ({ pageParam, signal }) => CustomerService.fetchTransactions(pageParam, signal),
      enabled: !!userId,
      initialPageParam: { page: 1 },
      getNextPageParam: (lastPage, allPages) => nextTransactionsCursor(lastPage, allPages.length),
    })

  const loaded = useMemo(() => data?.pages.flatMap(read => read.data) ?? [], [data])
  const isSearching = searchTerm.trim().length > 0
  // Only filters that narrow anything read the whole history; a mistyped range does not.
  const isFiltering = narrowsRows(filters)
  // Narrowed by either means the rows on screen are a subset of what was read, which changes how
  // they are paged and what the count beside the table has to say.
  const isNarrowed = isSearching || isFiltering
  // A sort the user picked has to order the customer's whole history, or its top row would only be
  // the top of what happened to be read. Like a search, it reads every page into the same list.
  const isSorted = !isDefaultSort(sortBy, sortOrder)
  const readsAll = isNarrowed || isSorted
  // Rows can be picked for a bulk alias only once they have been narrowed by description or by
  // amount, so an alias is set on a deliberate set of transactions rather than on whatever page
  // happens to be showing.
  const canSelect = canUpdateAliases && (isSearching || hasAmountFilter(filters))

  // The customer's connections and accounts name every institution and account they hold, so the
  // filters can offer all of them from the outset rather than only the ones a loaded transaction
  // happens to mention. Reference data only -- a failure here leaves the filters working off the
  // rows in hand, so it is deliberately not surfaced as a page error.
  const { data: directory } = useQuery({
    queryKey: connectionQueryKeys.transactionFilters(userId!),
    queryFn: () => BankService.fetchConnectionsWithAccounts(userId!),
    enabled: !!userId,
    staleTime: Infinity,
  })

  // Offered from the whole buffer rather than from the filtered rows, so choosing one value does
  // not strip the others out of the list they were chosen from.
  const options = useMemo(
    () =>
      filterOptions(loaded, {
        institutionName: directory?.connections.map(connection => connection.name),
        name: directory?.accounts.map(account => account.name),
        accountNumber: directory?.accounts.map(account => account.number),
      }),
    [loaded, directory]
  )
  const filtered = useMemo(() => applyFilters(loaded, filters), [loaded, filters])

  // The endpoint has no description filter, so a search narrows the rows read.
  const matches = useMemo(() => {
    if (!isSearching) {
      return filtered
    }

    const term = searchTerm.trim().toLowerCase()
    return filtered.filter(transaction => transaction.description?.toLowerCase().includes(term))
  }, [isSearching, searchTerm, filtered])
  const sorted = useMemo(
    () => sortTransactions(matches, sortBy, sortOrder),
    [matches, sortBy, sortOrder]
  )

  // Dropping the search and the amount filter drops the selection with them.
  useEffect(() => {
    if (!canSelect) {
      setSelected(new Set())
    }
  }, [canSelect])

  // Avoid carrying paging, search or filter state across customers.
  useEffect(() => {
    setPage(1)
    setSearchTerm('')
    setSearchPage(1)
    setFilters(EMPTY_FILTERS)
    setSortBy(DEFAULT_SORT_BY)
    setSortOrder(DEFAULT_SORT_ORDER)
    setSelected(new Set())
    setReview(null)
    setReviewFailure(undefined)
  }, [userId])

  // Search and filters are dropped on leaving the tab or the customer, so the pages they pulled in
  // are dropped with them. Coming back then refreshes one read, not every read the search made.
  // Trimmed straight away, so a quick return already finds one read. A read still in flight is
  // cancelled too: left running it would land after the trim and put the whole list back. The
  // cancel reverts the cache to what it held before that read, so it is trimmed again once settled.
  useEffect(
    () => () => {
      const queryKey = ['transactions', userId]
      const trim = () => queryClient.setQueryData<TransactionPages>(queryKey, toFirstRead)
      trim()
      void queryClient.cancelQueries({ queryKey, exact: true }).then(trim)
    },
    [queryClient, userId]
  )

  // A search or filter answers from the customer's whole history, not just the pages read so far:
  // the read is paged per account, so a match -- or an account picked from the directory -- can be
  // on any page. Every page is pulled into the same list the table pages through, so once read,
  // changing the term or filters is instant and clearing them pages from memory.
  const readCount = data?.pages.length ?? 0
  const requestCount = data?.pages.reduce((total, read) => total + (read.requests ?? 1), 0) ?? 0
  const isReadCapped = hasNextPage && requestCount >= MAX_NARROWED_REQUESTS
  const isReadingAll = readsAll && Boolean(hasNextPage) && !isReadCapped
  const isFullyRead = !hasNextPage

  // Pull the next read in as soon as the page being shown runs past what has been read, so the
  // only short page is the one at the end of the customer's transactions. Landing on the last page
  // read so far pulls the next one in too, while there is more to read, so the page after it is
  // already numbered in the paginator. While narrowed, keep reading until there is nothing left.
  const rowsNeeded = page * TRANSACTIONS_PAGE_SIZE
  const isOnLastLoadedPage = loaded.length <= rowsNeeded
  const needsMore = readsAll ? isReadingAll : isOnLastLoadedPage && Boolean(hasNextPage)
  // readCount is a dependency so each read that lands asks for the next, even when the in-flight
  // flag never shows as set in between.
  useEffect(() => {
    if (needsMore && !isFetchingNextPage && !isError) {
      fetchNextPage()
    }
  }, [needsMore, readCount, isFetchingNextPage, isError, fetchNextPage])

  const activeRows = sorted
  const activePage = readsAll ? searchPage : page
  const sliceStart = (activePage - 1) * TRANSACTIONS_PAGE_SIZE
  const rows = activeRows.slice(sliceStart, sliceStart + TRANSACTIONS_PAGE_SIZE)

  const hasPagesBehind = page > 1
  const hasPagesAhead = loaded.length > page * TRANSACTIONS_PAGE_SIZE || hasNextPage
  const isFillingPage = isFetchingNextPage && loaded.length < rowsNeeded
  // Only the pages read so far can be numbered: the read reports no total.
  const loadedPageCount = Math.ceil(loaded.length / TRANSACTIONS_PAGE_SIZE)
  // A narrowed view is held on the loader until every page has been read, so what it shows is the
  // complete answer rather than one that grows under the user.
  const isTableLoading = isLoading || (readsAll ? isReadingAll : isFillingPage)

  // Only rows the current search and filters still match count, so a row picked under an earlier
  // term is never updated without being on screen. The update is made one account at a time, so the
  // bar also says how many accounts the selection spans.
  const selectedRows = useMemo(
    () => sorted.filter(transaction => selected.has(transactionKey(transaction))),
    [sorted, selected]
  )
  const selectedAccountCount = useMemo(
    () => new Set(selectedRows.map(transaction => transaction.accountId)).size,
    [selectedRows]
  )

  const noun = (count: number) => (count === 1 ? 'transaction' : 'transactions')

  const handleApplyAlias = (alias: DescriptionAlias) => {
    setReviewFailure(undefined)
    setReview({ alias, rows: selectedRows })
  }

  const handleCancelReview = () => {
    // After a failure the rows on screen may be out of date, so they are read again on the way out.
    if (reviewFailure) {
      void reconcile()
    }
    setReview(null)
    setReviewFailure(undefined)
  }

  const handleConfirmReview = async () => {
    if (!review) return
    const { alias, rows } = review
    const startedFor = userId
    const { updated, failure } = await apply(rows, alias)

    // The operator moved to another customer while it ran. The update itself kept to the customer
    // it was for; there is just nothing on screen left to report it against.
    if (shownUserId.current !== startedFor) {
      return
    }
    const updatedCount = (reviewFailure?.updatedCount ?? 0) + updated.length

    if (failure) {
      // What went through is dropped from the selection and the review; what is left stays picked,
      // so trying again sends only that.
      const remaining = new Set(failure.remaining)
      setSelected(remaining)
      setReview({ alias, rows: rows.filter(row => remaining.has(row.transactionId)) })
      setReviewFailure({ message: errorMessage(failure.error), updatedCount })
      return
    }

    setReview(null)
    setReviewFailure(undefined)
    setSelected(new Set())
    setNotice(
      `Description alias set to ${aliasLabel(alias)} on ${updatedCount} ${noun(updatedCount)}.`
    )
  }

  const handleClearSelection = () => setSelected(new Set())

  const handleNextPage = () => setPage(current => current + 1)
  const handlePreviousPage = () => setPage(current => Math.max(1, current - 1))

  const handleSearch = (term: string) => {
    setSearchTerm(term)
    setSearchPage(1)
  }

  const handleFilters = (next: ITransactionFilters) => {
    setFilters(next)
    setSearchPage(1)
  }

  const handleClearSearch = () => {
    setSearchTerm('')
    setSearchPage(1)
  }

  const handleSort = (property: keyof ITransaction, order: Order) => {
    setSortBy(property)
    setSortOrder(order)
    // A reorder makes the place the user held meaningless, so go back to the top.
    setPage(1)
    setSearchPage(1)
  }

  if (isError) {
    return (
      <ErrorPage
        title={'Unable to Fetch Financial Transactions'}
        message={
          "We couldn't retrieve customer data at the moment. Please check your internet connection or try again later. If the issue persists, contact administrator."
        }
      />
    )
  }

  // A search that matched nothing is answered beside the table, which keeps the box up.
  const isEmpty = loaded.length === 0 && !isLoading && !isFetchingNextPage && !hasNextPage
  if (!isSearching && isEmpty) {
    return (
      <EmptyContainer>
        <Box component="img" src={SearchLogo} alt="Search Icon" mb={2} />
        <Title>No Transactions Recorded</Title>
        <Message>The Customer hasn't added any transactions yet</Message>
      </EmptyContainer>
    )
  }

  const narrowedScopeNote = () => {
    if (isReadingAll) {
      return `Searching all of this customer’s transactions… ${loaded.length} read so far`
    }
    // The read reports no total, so the note gives how many rows were searched rather than claiming
    // to know how many the customer holds.
    const searched = `${loaded.length} ${noun(loaded.length)} searched`
    return isFullyRead
      ? `${sorted.length} of ${searched}`
      : `${sorted.length} of the first ${searched} (stopped early)`
  }

  // Sorting reaches the rows read so far, which is worth saying while more remain -- otherwise the
  // top row reads as the customer's newest transaction outright.
  const sortedScopeNote = () => {
    if (isReadingAll) {
      return `Reading all of this customer’s transactions to sort… ${loaded.length} read so far`
    }
    return isFullyRead
      ? `Sorted across all ${loaded.length} ${noun(loaded.length)}`
      : `Sorted within the first ${loaded.length} ${noun(loaded.length)} (stopped early)`
  }

  const loadedScopeNote = isNarrowed
    ? narrowedScopeNote()
    : isSorted
      ? sortedScopeNote()
      : hasNextPage
        ? `Sorted within ${loaded.length} loaded transactions`
        : ''

  const noMatchesNote = () => {
    const scope = isFullyRead ? 'No transaction' : 'No loaded transaction'
    return isSearching
      ? `${scope} matches "${searchTerm.trim()}".`
      : `${scope} matches these filters.`
  }

  const narrowedPageCount = Math.ceil(sorted.length / TRANSACTIONS_PAGE_SIZE)
  const showNarrowedPaginator = readsAll && !isTableLoading && narrowedPageCount > 1
  const showCursorPaginator = !readsAll && (hasPagesAhead || hasPagesBehind)

  return (
    <Box>
      <Header>
        <Typography variant="h6">Transactions</Typography>
        <Box display="flex" alignItems="center" gap={2}>
          {loadedScopeNote && <Note>{loadedScopeNote}</Note>}
          <Search
            searchValue={searchTerm}
            onSearch={handleSearch}
            onClear={handleClearSearch}
            placeholder="Search transactions by description"
          />
        </Box>
      </Header>

      <TransactionFilters filters={filters} options={options} onChange={handleFilters} />

      {canUpdateAliases && !canSelect && loaded.length > 0 && (
        <Hint>
          To set a description alias on several transactions, search by description or filter by
          amount first.
        </Hint>
      )}

      {canSelect && selectedRows.length > 0 && (
        <BulkAliasBar
          selectedCount={selectedRows.length}
          accountCount={selectedAccountCount}
          onApply={handleApplyAlias}
          onClear={handleClearSelection}
          isApplying={isApplying}
        />
      )}

      <DataTable
        rows={rows}
        columns={transactionColumns}
        selection={
          canSelect
            ? {
                getRowId: transactionKey,
                getRowLabel: transactionLabel,
                selected,
                onChange: setSelected,
              }
            : undefined
        }
        loadingState={isTableLoading}
        onSort={handleSort}
        sortBy={sortBy}
        sortOrder={sortOrder}
      />

      {isNarrowed && sorted.length === 0 && !isTableLoading && (
        <NoMatches>{noMatchesNote()}</NoMatches>
      )}

      {(showCursorPaginator || showNarrowedPaginator) && (
        <Box
          display="flex"
          justifyContent="center"
          mt={2}
          py={1}
          sx={{
            border: '1px solid #E4E7EC',
            borderRadius: '8px',
          }}
        >
          {showNarrowedPaginator ? (
            <Paginator
              totalPages={narrowedPageCount}
              currentPage={searchPage}
              onChange={setSearchPage}
            />
          ) : (
            <CursorPaginator
              currentPage={page}
              loadedPageCount={loadedPageCount}
              disabled={isFillingPage}
              hasPreviousPage={hasPagesBehind && !isFillingPage}
              hasNextPage={hasPagesAhead && !isFillingPage}
              onPrevious={handlePreviousPage}
              onNext={handleNextPage}
              onPageChange={setPage}
            />
          )}
        </Box>
      )}

      {review && (
        <ReviewAliasDialog
          open
          rows={review.rows}
          alias={review.alias}
          isApplying={isApplying}
          progress={progress}
          failure={reviewFailure}
          onCancel={handleCancelReview}
          onConfirm={handleConfirmReview}
        />
      )}

      <Snackbar
        open={!!notice}
        autoHideDuration={6000}
        onClose={() => setNotice('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setNotice('')} sx={{ width: '100%' }}>
          {notice}
        </Alert>
      </Snackbar>
    </Box>
  )
}

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}))

const EmptyContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  border: `1px solid #E4E7EC`,
  borderRadius: '8px',
  padding: '3rem 0',
})

const Title = styled(Typography)(({ theme }) => ({
  ...theme.typography.h6,
  fontSize: '0.9rem',
  paddingBottom: theme.spacing(3),
}))

const Message = styled(Typography)(({ theme }) => ({
  ...theme.typography.body1,
  color: theme.palette.text.secondary,
  fontSize: '0.9rem',
  fontWeight: 500,
  maxWidth: 600,
  textAlign: 'center',
}))

// Small notes beside or under a section title, drawn as the other tabs draw theirs.
const Note = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.secondary.main,
  fontSize: '13px',
}))

const Hint = styled(Note)(({ theme }) => ({
  paddingBottom: theme.spacing(1.5),
}))

const NoMatches = styled(Message)(({ theme }) => ({
  maxWidth: 'none',
  padding: theme.spacing(3, 2),
  textAlign: 'center',
}))
